<template>
    <div v-if="isInitialising" class="brand-loader-wrapper">
        <brand-loader />
    </div>
    <div v-else class="document-viewer" :class="{ 'modal-mode': isModalMode }">
        <div class="breadcrumbs-holder" v-if="!props.noBreadcrumb">
            <router-link
                :to="documentListLink"
                class="mobile-link">
                <font-awesome-icon class="arrowLeft" :icon="['fas', 'arrow-left']" /> Back
            </router-link>
            <breadcrumb-with-underlining :breadcrumbItems="breadcrumbItems" class="breadcrumbs" />
        </div>
        <div class="main-content" :class="{ 'card-main': !isModalMode }">
            <report-details
                :allowZoom="false"
                :hasAccess="props.excelDetails.hasAccess"
                :description="props.excelDetails.description"
                :publication-date="props.excelDetails.publicationDate"
                :authors="props.excelDetails.authors"
                :filters="props.excelDetails.filters"
                :title="props.excelDetails.documentTitle"
                :subtitle="props.excelDetails.documentSubtitle"
                :access-reason="props.excelDetails.accessReason"
                :report-id="props.excelDetails.documentShortCode"
                report-type="Excel"
            />
            <div class="report-section" :class="{ 'no-access': !props.excelDetails.hasAccess }">
                <div
                    v-if="props.excelDetails.hasAccess"
                >
                    <primevue-button
                        @click="download"
                        class="plain-btn">
                        Download
                        <font-awesome-icon :icon="['fas', 'download']" />
                    </primevue-button>
                </div>
                <div class="no-access-window" v-if="!props.excelDetails.hasAccess">
                    <no-report-access />
                </div>
            </div>
            <document-comments
                v-if="isModalMode"
                class="modal-comments"
                :document-short-code="props.excelDetails.documentShortCode"
                :initial-comment-count="props.excelDetails.hasAccess ? props.excelDetails.commentCount : 0"
                :locked="!props.excelDetails.hasAccess"
                :allow-expand="props.allowCommentsExpand"
            />
        </div>
        <div v-if="!isModalMode" class="side-content">
            <div v-if="props.excelDetails.hasAccess" class="transpose-options">
                <primevue-button text class="plain-btn extra-padding grow" @click="showShareModal = true">
                    Share
                    <font-awesome-icon :icon="['fas', 'share-nodes']" />
                </primevue-button>
            </div>
            <div class="options">
                <document-comments
                    :document-short-code="props.excelDetails.documentShortCode"
                    :initial-comment-count="props.excelDetails.hasAccess ? props.excelDetails.commentCount : 0"
                    :locked="!props.excelDetails.hasAccess"
                    compact
                />
            </div>
            <div class="options">
                <div class="title">Contact us</div>
                <div class="description">
                    Let us know if you have any questions about this report, or research service.
                </div>
                <our-experts-section :our-experts="ourExperts" />
                <div>
                    <primevue-button
                        text
                        @click="openContactUs"
                        class="plain-btn">
                        Speak to our experts
                        <font-awesome-icon :icon="['fas', 'user']" />
                    </primevue-button>
                </div>
            </div>
        </div>
    </div>
    <contact-us v-model:visible="showContactUsModal" @close="showContactUsModal = false" :documentTitle="props.excelDetails.documentTitle" :our-experts="ourExperts" />
    <central-modal
        v-model:visible="showShareModal"
        header="Share"
        :size="{ width: '70rem', height: '32rem' }"
    >
        <share-document-url :title="props.excelDetails.documentTitle" />
    </central-modal>
</template>

<script lang="ts" setup>

import { IExcelDetails } from '@/interfaces/deltaGpt/IDocument.ts';
import primevueButton from 'primevue/button';
import { getResearchSectionRouteName } from '@/routes/routeUtils.ts';
import { computed, ref } from 'vue';
import { IBreadcrumbItem } from '@/interfaces/general/IBreadcrumbItem.ts';
import { createDocumentBreadcrumbItems } from '@/utils/documentBreadcrumbUtils.ts';
import { router } from '@/router.ts';
import BreadcrumbWithUnderlining from '@/components/common/BreadcrumbWithUnderlining.vue';
import { deltaGptApi } from '@/api/deltaGpt/deltaGptApi.ts';
import { IExpert } from '@/interfaces/deltaGpt/IExpert.ts';
import { downloadArrayBufferFile } from '@/utils/Base64Utils.ts';
import { useRoute } from 'vue-router';
import { deltaGptRoutes } from '@/routes/deltaGptRoutes.ts';
import { getExcelDownloadFileName } from '@/utils/excelFileName.ts';
import CentralModal from '@/components/common/CentralModal.vue';
import BrandLoader from '@/components/layout/BrandLoader.vue';
import ShareDocumentUrl from './ShareDocumentUrl.vue';
import ReportDetails from './ReportDetails.vue';
import DocumentComments from './DocumentComments.vue';
import NoReportAccess from './NoReportAccess.vue';
import OurExpertsSection from './OurExperts.vue';
import ContactUs from './ContactUs.vue';

const route = useRoute();

const props = withDefaults(defineProps<{
    excelDetails: IExcelDetails;
    noBreadcrumb?: boolean;
    displayMode?: 'page' | 'modal';
    allowCommentsExpand?: boolean;
}>(), {
    noBreadcrumb: false,
    displayMode: 'page',
    allowCommentsExpand: true,
});

const ourExperts = ref<Array<IExpert>>([]);
const showContactUsModal = ref<boolean>(false);
const showShareModal = ref<boolean>(false);
const isInitialising = ref<boolean>(false);
const isModalMode = computed<boolean>(() => props.displayMode === 'modal');

const documentListLink = computed(() => ({
    name: getResearchSectionRouteName(route, deltaGptRoutes.documentList.name),
    query: route.query,
}));

const breadcrumbItems = computed<Array<IBreadcrumbItem>>(() => createDocumentBreadcrumbItems(
    props.excelDetails.documentTitle,
    props.excelDetails.documentShortCode,
    (): void => {
        router.push(documentListLink.value);
    },
));

function openContactUs (): void {
    showContactUsModal.value = true;
}

async function download (): Promise<void> {
    if (!props.excelDetails.hasAccess) return;
    const excelFile = await deltaGptApi.getExcelFile(props.excelDetails.documentShortCode);
    downloadArrayBufferFile(excelFile, getExcelDownloadFileName(props.excelDetails.documentTitle, props.excelDetails.fileType), 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
}

async function initialise (): Promise<void> {
    isInitialising.value = true;

    try {
        // TODO: sort
        ourExperts.value = await deltaGptApi.getExperts({ subscriptions: [] });
    } finally {
        isInitialising.value = false;
    }
}

initialise();

</script>

<style lang="scss" scoped>
@use '/src/assets/scss/components/research/documentView.scss';

.brand-loader-wrapper {
    position: relative;
    height: 100%;
    min-height: 0;
}

.document-viewer {

    &.modal-mode {
        grid-template-columns: minmax(0, 1fr);
        grid-template-areas: "main";
        overflow-y: auto;

        .main-content {
            margin: 0;
        }
    }
    .main-content {
        align-self: start;
        padding: var(--largeSpacing);
        margin: var(--extraLargeSpacing);
    }
}

.report-section {
    // height: 100%;
    overflow-y: hidden !important;
    padding-bottom: var(--largeSpacing);
    height: auto;
    &.no-access {
        padding-top: 10rem;
    }
}

.modal-comments {
    border-top: 1px solid var(--neutral300);
    margin-top: var(--largeSpacing);
    padding-top: var(--largeSpacing);
}

.side-content {
    .transpose-options {
        display: flex;
        justify-content: space-between;
        gap: var(--defaultSpacing);

        .grow {
            flex: 1;
            display: flex;
        }
    }
}

</style>
